<template>
    <div class="user my-camp-order-detail camp-member-page">
        <div class="headerbg">
            <b-container class="d-flex align-items-center justify-content-between">
                <h1>{{ title }}</h1>
                <b-button variant="outline-secondary" size="sm" @click="logout">登出</b-button>
            </b-container>
        </div>
        <b-container fluid="xl">
            <b-row>
                <b-col cols="12" md="2" class="member-sidebar-col">
                    <nacUser></nacUser>
                </b-col>
                <b-col cols="12" md="10" class="member-content-col">
                    <b-button variant="link" class="px-0 mb-2" @click="goBack">
                        <b-icon icon="chevron-left"></b-icon> 返回訂單總覽
                    </b-button>

                    <div v-if="pageLoading" class="text-center py-5">
                        <b-spinner></b-spinner>
                    </div>

                    <div v-else-if="loadError" class="text-center py-5">
                        <p class="text-muted">{{ loadError }}</p>
                        <b-button variant="primary" @click="goBack">返回訂單總覽</b-button>
                    </div>

                    <template v-else-if="detailOrder">
                        <CampOrderPanel :order="detailOrder" :submitting="submitting" :bank-info="bankInfo"
                            :refunds="detailOrderRefunds" :refunds-loading="refundsLoading" :refunds-load-error="refundsLoadError"
                            @reported="onReported" @retry="submitRetry" @cancel-detail="openCancelDetailModal"
                            @view-proof="viewProof" @apply-refund="openApplyRefund" @refund-changed="onRefundChanged"
                            @cancel-order="openCancelOrderModal" @pay-surcharge="goPaySurcharge" @reload-refunds="loadRefunds">
                        </CampOrderPanel>
                    </template>
                </b-col>
            </b-row>
        </b-container>

        <!-- 付款截圖檢視 Modal -->
        <b-modal id="modal-proof-view" title="付款截圖" size="lg" hide-footer @hidden="resetProofView">
            <div class="text-center">
                <b-img v-if="proofViewUrl" :src="proofViewUrl" fluid></b-img>
                <div v-else class="text-muted py-4">讀取中...</div>
            </div>
        </b-modal>

        <!-- 申請退費 Modal -->
        <b-modal id="modal-apply-refund" :title="applyTarget && applyTarget.detail ? '申請單筆退費' : '申請整筆退費'" size="md"
            ok-title="送出申請" ok-variant="primary" :ok-disabled="applySubmitting || !applyForm.method"
            cancel-title="返回" @ok.prevent="submitApplyRefund" @hidden="resetApplyForm">
            <p class="small text-muted">
                送出後會由承辦人員回覆退費金額並正式取消對應報名。<br>
                轉帳退費在承辦回覆後，需再補填收款帳戶與帳戶證明。
            </p>
            <div v-if="applyTarget && applyTarget.detail" class="mb-2 small">
                <span class="text-muted">退費對象：</span>
                {{ applyTarget.detail.RelationName }}｜{{ applyTarget.detail.CampProgramTitle }} / {{ applyTarget.detail.CampProductTitle }}
            </div>
            <b-form-group label="退費方式">
                <b-form-radio-group v-model="applyForm.method" :options="refundMethodOptions" stacked></b-form-radio-group>
            </b-form-group>
            <b-form-group label="申請原因（選填）">
                <b-form-textarea v-model="applyForm.reason" rows="2" placeholder="可留空"></b-form-textarea>
            </b-form-group>
        </b-modal>

        <!-- 取消整筆訂單 Modal -->
        <b-modal id="modal-cancel-order" title="取消整筆訂單" size="md"
            ok-title="確認取消訂單" ok-variant="danger" :ok-disabled="submitting"
            cancel-title="返回" @ok.prevent="submitCancelOrder" @hidden="resetCancelOrderForm">
            <p>確定要取消整筆訂單嗎？此訂單底下所有尚未取消的報名都會一併取消。</p>
            <b-form-group label="取消原因（選填）">
                <b-form-textarea v-model="cancelOrderForm.CancelReason" rows="2" placeholder="可留空"></b-form-textarea>
            </b-form-group>
        </b-modal>

        <!-- 重新付款選收款方式 Modal -->
        <b-modal id="modal-retry-payment" title="重新付款" size="md"
            ok-title="確認" ok-variant="primary" :ok-disabled="submitting || !retryForm.PaymentMethod"
            cancel-title="返回" @ok.prevent="confirmRetry" @hidden="resetRetryForm">
            <p class="small text-muted">請選擇收款方式，送出後不可變更。</p>
            <b-form-group label="收款方式">
                <b-form-radio-group v-model="retryForm.PaymentMethod" :options="retryPaymentMethodOptions" stacked></b-form-radio-group>
            </b-form-group>
        </b-modal>

        <!-- 取消單筆報名明細 Modal -->
        <b-modal id="modal-cancel-detail" title="取消報名明細" size="md"
            ok-title="確認取消" ok-variant="danger" :ok-disabled="submitting"
            cancel-title="返回" @ok.prevent="submitCancelDetail" @hidden="resetCancelDetailForm">
            <p>確定要取消這筆未付款的報名明細嗎？</p>
            <p v-if="cancelDetailWarning" class="text-danger">{{ cancelDetailWarning }}</p>
            <b-form-group label="取消原因（選填）" label-cols-sm="4">
                <b-form-textarea v-model="cancelDetailForm.CancelReason" rows="2" placeholder="可留空"></b-form-textarea>
            </b-form-group>
        </b-modal>
    </div>
</template>
<script>
import nacUser from '@/components/nacUser.vue';
import CampOrderPanel from '@/components/CampOrderPanel.vue';
import {
    apiCampOrderDetail,
    apiCampOrderCancel,
    apiCampPaymentRetry,
    apiCampPaymentProofImage,
    apiCampRegistrationCancel,
    apiGetInformation,
    apiCampRefundList,
    apiCampRefundApplyOrder,
    apiCampRefundApplyDetail,
} from '@/api/API.js';
import { CampRefundMethodOptions, CampPaymentMethodOptions } from '@/utils/campEnums.js';
import memberLoginContinuation from '@/mixins/memberLoginContinuation';

export default {
    name: 'MyCampOrderDetail',
    mixins: [memberLoginContinuation],
    components: { nacUser, CampOrderPanel },
    data() {
        return {
            title: '訂單明細',
            orderId: null,
            detailOrder: null,
            detailLoading: false,
            refundsLoading: true,
            refundsLoadError: false,
            loadError: '',
            bankInfo: null,
            submitting: false,
            proofViewUrl: null,
            allRefunds: [],
            refundMethodOptions: CampRefundMethodOptions,
            applyTarget: null,
            applyForm: { method: null, reason: '' },
            applySubmitting: false,
            cancelDetailTarget: null,
            cancelDetailWarning: '',
            cancelDetailForm: { CancelReason: '' },
            cancelOrderTargetId: null,
            cancelOrderForm: { CancelReason: '' },
            retryTarget: null,
            retryForm: { PaymentMethod: null },
            retryPaymentMethodOptions: CampPaymentMethodOptions,
        };
    },
    computed: {
        pageLoading() {
            return this.detailLoading || this.refundsLoading;
        },
        detailOrderRefunds() {
            if (!this.orderId) return [];
            return this.allRefunds.filter((r) => r.CampOrderId === this.orderId);
        },
    },
    mounted() {
        if (this.IsNull(this._sessionData('TokenID')) == '') {
            this.requestMemberLogin(this.initializePage);
            return;
        }
        this.initializePage();
    },
    methods: {
        initializePage() {
        this.orderId = this.$route.query.orderId || null;
        if (!this.orderId) {
            this.goBack();
            return;
        }
        this.loadDetail();
        this.loadBankInfo();
        this.loadRefunds(true);
        },
        logout() {
            const data1 = this._storeData('Header_form1');
            if (this.IsNull(data1) !== '') {
                data1.text = '學員登入';
            }
            this._sessionData('NewToken', '');
            this._sessionData('TokenID', '');
            this._sessionData('campCartCachedCount', '');
            this._sessionData('courseCartCachedCount', '');
            this._go('/');
        },
        goBack() {
            this.$router.push({ path: '/campOrder' });
        },
        goPaySurcharge(s) {
            if (!s || !s.PaymentId) return;
            this.$router.push({ name: 'CampPaymentReport', params: { paymentId: s.PaymentId } });
        },
        loadDetail(silent = false) {
            if (!silent) this.detailLoading = true;
            this.loadError = '';
            return apiCampOrderDetail(this.orderId)
                .then((res) => {
                    this.detailOrder = res.data;
                    this.detailLoading = false;
                })
                .catch((err) => {
                    this.detailLoading = false;
                    this.loadError = err && err.response && err.response.status === 404
                        ? '找不到這筆訂單，或它不屬於你的帳號'
                        : '讀取訂單資料失敗，請稍後再試';
                });
        },
        loadBankInfo() {
            const types = ['camp_bank_name', 'camp_bank_branch', 'camp_account_name', 'camp_account_no', 'camp_payment_info'];
            Promise.all(types.map((t) => apiGetInformation(t).catch(() => null))).then(
                ([name, branch, accName, accNo, info]) => {
                    this.bankInfo = {
                        bankName: name?.data?.Content || '',
                        bankBranch: branch?.data?.Content || '',
                        accountName: accName?.data?.Content || '',
                        accountNo: accNo?.data?.Content || '',
                        paymentInfoHtml: info?.data?.Content || '',
                    };
                },
            );
        },
        loadRefunds(showPageLoading = false) {
            if (showPageLoading) this.refundsLoading = true;
            this.refundsLoadError = false;
            apiCampRefundList()
                .then((res) => {
                    this.allRefunds = res.data || [];
                    this.refundsLoading = false;
                })
                .catch(() => {
                    this.allRefunds = [];
                    this.refundsLoading = false;
                    this.refundsLoadError = true;
                });
        },
        viewProof(paymentId) {
            this.proofViewUrl = null;
            this.$bvModal.show('modal-proof-view');
            apiCampPaymentProofImage(paymentId)
                .then((res) => {
                    this.proofViewUrl = URL.createObjectURL(res.data);
                })
                .catch(() => {
                    this.$bvModal.hide('modal-proof-view');
                });
        },
        resetProofView() {
            if (this.proofViewUrl) {
                URL.revokeObjectURL(this.proofViewUrl);
                this.proofViewUrl = null;
            }
        },
        onReported() {
            this.loadDetail(true);
        },
        openApplyRefund(payload) {
            this.applyTarget = payload || {};
            this.applyForm = { method: null, reason: '' };
            this.$bvModal.show('modal-apply-refund');
        },
        resetApplyForm() {
            this.applyTarget = null;
            this.applyForm = { method: null, reason: '' };
        },
        submitApplyRefund() {
            if (!this.applyTarget || !this.applyForm.method) return;
            const body = { RefundMethod: this.applyForm.method, RefundType: 1, Reason: this.applyForm.reason || '' };
            const req = this.applyTarget.detail
                ? apiCampRefundApplyDetail(this.applyTarget.detail.Id, body)
                : apiCampRefundApplyOrder(this.applyTarget.order.Id, body);
            this.applySubmitting = true;
            req
                .then(() => {
                    this.applySubmitting = false;
                    this.$bvModal.hide('modal-apply-refund');
                    this.loadRefunds();
                    this.loadDetail(true);
                })
                .catch((err) => {
                    this.applySubmitting = false;
                    alert((err && err.response && err.response.data && err.response.data.error) || '退費申請送出失敗，請稍後再試');
                });
        },
        onRefundChanged() {
            this.loadRefunds();
            this.loadDetail(true);
        },
        submitRetry(order) {
            this.retryTarget = order;
            this.retryForm = { PaymentMethod: null };
            this.$bvModal.show('modal-retry-payment');
        },
        resetRetryForm() {
            this.retryTarget = null;
            this.retryForm = { PaymentMethod: null };
        },
        confirmRetry() {
            if (!this.retryTarget || !this.retryForm.PaymentMethod) return;
            this.submitting = true;
            apiCampPaymentRetry(this.retryTarget.Id, { PaymentMethod: this.retryForm.PaymentMethod })
                .then(() => {
                    this.submitting = false;
                    this.$bvModal.hide('modal-retry-payment');
                    this.loadDetail(true);
                })
                .catch(() => {
                    this.submitting = false;
                });
        },
        openCancelDetailModal(order, detail) {
            this.cancelDetailTarget = { orderId: order.Id, detailId: detail.Id };
            this.cancelDetailWarning = detail.IsCampAddon
                ? '此項目為加購項目，取消前請確認訂單內容。'
                : '';
            this.$bvModal.show('modal-cancel-detail');
        },
        resetCancelDetailForm() {
            this.cancelDetailTarget = null;
            this.cancelDetailWarning = '';
            this.cancelDetailForm = { CancelReason: '' };
        },
        openCancelOrderModal(order) {
            this.cancelOrderTargetId = order.Id;
            this.cancelOrderForm = { CancelReason: '' };
            this.$bvModal.show('modal-cancel-order');
        },
        resetCancelOrderForm() {
            this.cancelOrderTargetId = null;
            this.cancelOrderForm = { CancelReason: '' };
        },
        submitCancelOrder() {
            if (!this.cancelOrderTargetId) return;
            this.submitting = true;
            apiCampOrderCancel(this.cancelOrderTargetId, { CancelReason: this.cancelOrderForm.CancelReason })
                .then(() => {
                    this.submitting = false;
                    this.$bvModal.hide('modal-cancel-order');
                    this.loadDetail(true);
                })
                .catch((err) => {
                    this.submitting = false;
                    alert((err && err.response && err.response.data) || '取消訂單失敗，請稍後再試');
                });
        },
        submitCancelDetail() {
            if (!this.cancelDetailTarget) return;
            this.submitting = true;
            apiCampRegistrationCancel(this.cancelDetailTarget.detailId, { CancelReason: this.cancelDetailForm.CancelReason })
                .then(() => {
                    this.submitting = false;
                    this.$bvModal.hide('modal-cancel-detail');
                    this.loadDetail(true);
                })
                .catch(() => {
                    this.submitting = false;
                });
        },
    },
};
</script>
